import { Warning } from "@phosphor-icons/react";

export const RACI_MARKERS = ["", "R", "A", "C", "I", "E"];
export const MARKER_LABEL = { R: "Responsible", A: "Accountable", C: "Consulted", I: "Informed", E: "Enabler / Executor" };
export const MARKER_CLS = {
  R: "bg-blue-100 text-blue-700",
  A: "bg-red-100 text-red-700",
  C: "bg-slate-100 text-slate-600",
  I: "bg-slate-100 text-slate-500",
  E: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300",
};

// Returns governance issues for a step: count of Accountable markers must be exactly 1.
export function stepAccountabilityIssue(step) {
  const a = Object.values(step.assignments || {}).filter((m) => m === "A").length;
  if (a === 0) return { type: "none", msg: "No Accountable (A) owner" };
  if (a > 1) return { type: "multi", msg: `${a} Accountable (A) owners — single point of accountability required` };
  return null;
}

export default function RaciEBlueprint({ doc }) {
  const p = doc.payload || {};
  const roles = (p.roles || []).filter((r) => r.name?.trim());
  const steps = (p.steps || []).filter((s) => s.name?.trim());
  const issues = steps.map((s) => ({ step: s, issue: stepAccountabilityIssue(s) })).filter((x) => x.issue);

  return (
    <div data-testid="racie-blueprint">
      {p.context && <p className="mb-5 text-sm text-slate-500 max-w-2xl">{p.context}</p>}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-left text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5 font-semibold sticky left-0 bg-slate-50">Step / Activity</th>
              {roles.map((r) => (
                <th key={r.id} className="px-3 py-2.5 font-semibold text-center whitespace-nowrap">{r.name}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {steps.length === 0 && <tr><td colSpan={roles.length + 1} className="px-4 py-6 text-center text-slate-300">No steps recorded.</td></tr>}
            {steps.map((s) => {
              const issue = stepAccountabilityIssue(s);
              return (
                <tr key={s.id} data-testid={`racie-row-${s.id}`} className={issue ? "bg-amber-50 border-l-4 border-l-amber-500" : ""}>
                  <td className="px-4 py-3 text-slate-800 font-medium sticky left-0 bg-inherit">
                    {s.name}
                    {issue && <span data-testid={`racie-flag-${s.id}`} className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-500 text-white align-middle"><Warning size={11} weight="fill" /> {issue.type === "none" ? "No A" : "Multiple A"}</span>}
                  </td>
                  {roles.map((r) => {
                    const m = (s.assignments || {})[r.id] || "";
                    return (
                      <td key={r.id} className="px-3 py-3 text-center">
                        {m ? <span className={`inline-flex w-7 h-7 items-center justify-center rounded-sm font-mono text-xs font-bold ${MARKER_CLS[m]}`} title={MARKER_LABEL[m]}>{m}</span> : <span className="text-slate-300">·</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[11px] text-slate-500">
        {Object.entries(MARKER_LABEL).map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`inline-flex w-5 h-5 items-center justify-center rounded-sm text-[10px] font-bold ${MARKER_CLS[k]}`}>{k}</span>{label}
          </span>
        ))}
      </div>

      {issues.length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="racie-governance-summary">
          <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-wide text-amber-800"><Warning size={16} weight="duotone" /> Accountability Governance Gaps</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {issues.map(({ step, issue }) => (
              <li key={step.id}><span className="font-medium">{step.name}</span> — {issue.msg}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
